import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Shield, Trash, Calendar, RefreshCw, Send, Edit, Plus } from 'lucide-react';
import { supabaseService } from '@/services/supabaseService';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface Invitation {
  id: string;
  email: string;
  status: 'pending' | 'accepted' | 'expired';
  role: 'user' | 'admin';
  is_free_account: boolean;
  expiry_date: string | null;
  invited_by: string;
  created_at: string;
  updated_at: string;
  inviter?: {
    id: string;
    email: string;
    username: string;
  };
}

interface CreateInvitationForm {
  email: string;
  role: 'user' | 'admin';
  isFreeAccount: boolean;
  expiryDate: string | null;
}

interface EditInvitationForm extends CreateInvitationForm {
  id: string;
}

const InvitationsAdmin = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0, hasMore: false });
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [selectedInvitationId, setSelectedInvitationId] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState<CreateInvitationForm>({
    email: '',
    role: 'user',
    isFreeAccount: true,
    expiryDate: null,
  });
  const [editForm, setEditForm] = useState<EditInvitationForm>({
    id: '',
    email: '',
    role: 'user',
    isFreeAccount: true,
    expiryDate: null,
  });
  const [activeTab, setActiveTab] = useState<'pending' | 'accepted' | 'expired' | 'all'>('pending');

  useEffect(() => {
    checkAdminAccess();
    loadInvitations();
  }, [activeTab]);

  const checkAdminAccess = async () => {
    try {
      const { data: profile } = await supabaseService.getProfile();
      if (profile?.role !== 'admin') {
        toast({
          title: "Access Denied",
          description: "You don't have permission to access this page.",
          variant: "destructive"
        });
        navigate('/');
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
      navigate('/');
    }
  };

  const loadInvitations = async () => {
    try {
      setIsLoading(true);
      const status = activeTab !== 'all' ? activeTab : undefined;
      
      console.log(`Attempting to load invitations with status: ${status || 'all'}`);
      
      const result = await supabaseService.listInvitations(status, pagination.limit, pagination.offset);
      
      console.log('Invitations API response:', result);
      
      if (!result.success) {
        throw result.error || new Error('Failed to load invitations');
      }

      setInvitations(result.invitations || []);
      setPagination(result.pagination || { total: 0, limit: 20, offset: 0, hasMore: false });
    } catch (error) {
      console.error('Error loading invitations:', error);
      toast({
        title: "Error",
        description: "Failed to load invitations. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateInvitation = async () => {
    try {
      setIsLoading(true);
      
      if (!createForm.email) {
        toast({
          title: "Error",
          description: "Please enter an email address.",
          variant: "destructive"
        });
        return;
      }

      const { success, error } = await supabaseService.createInvitation(
        createForm.email,
        createForm.role,
        createForm.isFreeAccount,
        createForm.expiryDate || undefined
      );

      if (!success) {
        throw error || new Error('Failed to create invitation');
      }

      toast({
        title: "Success",
        description: `Invitation created for ${createForm.email}`,
      });
      
      setShowCreateDialog(false);
      setCreateForm({
        email: '',
        role: 'user',
        isFreeAccount: true,
        expiryDate: null,
      });
      
      await loadInvitations();
    } catch (error) {
      console.error('Error creating invitation:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create invitation",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditInvitation = async () => {
    try {
      setIsLoading(true);
      
      if (!editForm.id || !editForm.email) {
        toast({
          title: "Error",
          description: "Invalid invitation data",
          variant: "destructive"
        });
        return;
      }

      const { success, error } = await supabaseService.updateInvitation(
        editForm.id,
        {
          email: editForm.email,
          role: editForm.role,
          isFreeAccount: editForm.isFreeAccount,
          expiryDate: editForm.expiryDate
        }
      );

      if (!success) {
        throw error || new Error('Failed to update invitation');
      }

      toast({
        title: "Success",
        description: `Invitation updated successfully`,
      });
      
      setShowEditDialog(false);
      await loadInvitations();
    } catch (error) {
      console.error('Error updating invitation:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update invitation",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteInvitation = async () => {
    try {
      if (!selectedInvitationId) return;
      
      setIsLoading(true);
      
      const { success, error } = await supabaseService.deleteInvitation(selectedInvitationId);

      if (!success) {
        throw error || new Error('Failed to delete invitation');
      }

      toast({
        title: "Success",
        description: "Invitation deleted successfully",
      });
      
      setShowDeleteDialog(false);
      setSelectedInvitationId(null);
      await loadInvitations();
    } catch (error) {
      console.error('Error deleting invitation:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete invitation",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetToNormalAccount = async () => {
    try {
      if (!selectedInvitationId) return;
      
      setIsLoading(true);
      
      const { success, error } = await supabaseService.resetToNormalAccount(selectedInvitationId);

      if (!success) {
        throw error || new Error('Failed to reset account');
      }

      toast({
        title: "Success",
        description: "Account reset to normal status successfully",
      });
      
      setShowResetDialog(false);
      setSelectedInvitationId(null);
      await loadInvitations();
    } catch (error) {
      console.error('Error resetting account:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to reset account",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openEditDialog = (invitation: Invitation) => {
    setEditForm({
      id: invitation.id,
      email: invitation.email,
      role: invitation.role,
      isFreeAccount: invitation.is_free_account,
      expiryDate: invitation.expiry_date
    });
    setShowEditDialog(true);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Pending</Badge>;
      case 'accepted':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Accepted</Badge>;
      case 'expired':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  const getRoleBadge = (role: string) => {
    return role === 'admin' ? (
      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Admin</Badge>
    ) : (
      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">User</Badge>
    );
  };

  return (
    <div className="container mx-auto py-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => navigate('/admin')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">Invitations Management</h1>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Invitation
        </Button>
      </div>

      <Card className="mb-8">
        <CardHeader className="pb-2">
          <CardTitle>Manage Free Account Invitations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Tabs defaultValue="pending" value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
              <TabsList className="mb-4">
                <TabsTrigger value="pending">Pending</TabsTrigger>
                <TabsTrigger value="accepted">Accepted</TabsTrigger>
                <TabsTrigger value="expired">Expired</TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>
              
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Free Account</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      [...Array(3)].map((_, i) => (
                        <TableRow key={i}>
                          <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-10" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                        </TableRow>
                      ))
                    ) : invitations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-6 text-gray-500">
                          No invitations found
                        </TableCell>
                      </TableRow>
                    ) : (
                      invitations.map((invitation) => (
                        <TableRow key={invitation.id}>
                          <TableCell>{invitation.email}</TableCell>
                          <TableCell>{getStatusBadge(invitation.status)}</TableCell>
                          <TableCell>{getRoleBadge(invitation.role)}</TableCell>
                          <TableCell>
                            {invitation.is_free_account ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Yes</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">No</Badge>
                            )}
                          </TableCell>
                          <TableCell>{formatDate(invitation.expiry_date)}</TableCell>
                          <TableCell>{formatDate(invitation.created_at)}</TableCell>
                          <TableCell>
                            <div className="flex space-x-2">
                              <Button 
                                variant="outline" 
                                size="icon" 
                                onClick={() => openEditDialog(invitation)}
                                title="Edit invitation"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              
                              <Button 
                                variant="outline" 
                                size="icon" 
                                className="text-red-600 hover:text-red-700"
                                onClick={() => {
                                  setSelectedInvitationId(invitation.id);
                                  setShowDeleteDialog(true);
                                }}
                                title="Delete invitation"
                              >
                                <Trash className="h-4 w-4" />
                              </Button>
                              
                              {invitation.status === 'accepted' && (
                                <Button 
                                  variant="outline" 
                                  size="icon"
                                  onClick={() => {
                                    setSelectedInvitationId(invitation.id);
                                    setShowResetDialog(true);
                                  }}
                                  title="Reset to normal account"
                                >
                                  <RefreshCw className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              
              {!isLoading && pagination.total > 0 && (
                <div className="flex justify-between items-center mt-4">
                  <div className="text-sm text-gray-500">
                    Showing {pagination.offset + 1} to{' '}
                    {Math.min(pagination.offset + pagination.limit, pagination.total)} of{' '}
                    {pagination.total} results
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.offset === 0}
                      onClick={() => {
                        setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
                        loadInvitations();
                      }}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!pagination.hasMore}
                      onClick={() => {
                        setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
                        loadInvitations();
                      }}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {/* Create Invitation Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Invitation</DialogTitle>
            <DialogDescription>
              Create a free account invitation for a user. They will be able to register without payment.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                type="email" 
                value={createForm.email} 
                onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="user@example.com" 
                required 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role">User Role</Label>
              <Select 
                value={createForm.role} 
                onValueChange={(value) => setCreateForm(prev => ({ ...prev, role: value as 'user' | 'admin' }))}
              >
                <SelectTrigger id="role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Regular User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch 
                id="free-account" 
                checked={createForm.isFreeAccount}
                onCheckedChange={(checked) => setCreateForm(prev => ({ ...prev, isFreeAccount: checked }))}
              />
              <Label htmlFor="free-account">Free Account</Label>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="expiry-date">Expiry Date (optional)</Label>
              <Input 
                id="expiry-date" 
                type="date" 
                value={createForm.expiryDate || ''} 
                onChange={(e) => setCreateForm(prev => ({ ...prev, expiryDate: e.target.value || null }))}
              />
              <p className="text-sm text-gray-500">Leave empty for no expiration</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateInvitation} disabled={isLoading}>
              {isLoading ? "Creating..." : "Create Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Invitation Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Invitation</DialogTitle>
            <DialogDescription>
              Update the invitation details.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input 
                id="edit-email" 
                type="email" 
                value={editForm.email} 
                onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="user@example.com" 
                required 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-role">User Role</Label>
              <Select 
                value={editForm.role} 
                onValueChange={(value) => setEditForm(prev => ({ ...prev, role: value as 'user' | 'admin' }))}
              >
                <SelectTrigger id="edit-role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Regular User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch 
                id="edit-free-account" 
                checked={editForm.isFreeAccount}
                onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, isFreeAccount: checked }))}
              />
              <Label htmlFor="edit-free-account">Free Account</Label>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-expiry-date">Expiry Date (optional)</Label>
              <Input 
                id="edit-expiry-date" 
                type="date" 
                value={editForm.expiryDate || ''} 
                onChange={(e) => setEditForm(prev => ({ ...prev, expiryDate: e.target.value || null }))}
              />
              <p className="text-sm text-gray-500">Leave empty for no expiration</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditInvitation} disabled={isLoading}>
              {isLoading ? "Updating..." : "Update Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this invitation. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDialog(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteInvitation} className="bg-red-600 hover:bg-red-700">
              {isLoading ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to Normal Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will convert the free account to a normal account, requiring payment for continued access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowResetDialog(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetToNormalAccount}>
              {isLoading ? "Resetting..." : "Reset Account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InvitationsAdmin; 